import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Link,
} from '@mui/material';

interface TermsOfServiceDialogProps {
  open: boolean;
  onClose: () => void;
}

const TermsOfServiceDialog: React.FC<TermsOfServiceDialogProps> = ({ open, onClose }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle>
        <Typography variant="h5" component="h2">
          Terms of Service
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Effective Date: November 6, 2025
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ maxHeight: 'calc(90vh - 200px)', overflow: 'auto', pr: 1 }}>
          <Typography variant="body2" paragraph sx={{ fontWeight: 'bold' }}>
            Welcome to Waka Running! We want you to know and understand your rights and our rights 
            relating to the provision of the Services (as defined below). Please review them carefully.
          </Typography>

          <Typography variant="body2" paragraph sx={{ fontWeight: 'bold', mt: 2 }}>
            Here's a summary of some key terms:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <Typography component="li" variant="body2" paragraph>
              We respect your privacy. See how we collect, use, share, and protect your personal information in our Privacy Policy.
            </Typography>
            <Typography component="li" variant="body2" paragraph>
              You must be at least 13 years old or meet the minimum age requirement in your country to create an account.
            </Typography>
            <Typography component="li" variant="body2" paragraph>
              You can cancel your subscription or delete your account at any time.
            </Typography>
            <Typography component="li" variant="body2" paragraph>
              You own your content, but give us a right to use it.
            </Typography>
            <Typography component="li" variant="body2" paragraph>
              You must comply with our Acceptable Use Policy and we can disable access to your account or content if you act in violation of our rules.
            </Typography>
            <Typography component="li" variant="body2" paragraph>
              Waka Running is not liable for your activities and no warranties are made by Waka Running.
            </Typography>
            <Typography component="li" variant="body2" paragraph>
              We can suspend or terminate your account if you violate our Terms.
            </Typography>
            <Typography component="li" variant="body2" paragraph>
              You agree to arbitrate if there is a dispute between us (unless you reside in the European Union).
            </Typography>
          </Box>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            Terms of Service
          </Typography>
          
          <Typography variant="body2" paragraph>
            The Waka Running websites, related mobile applications and services (collectively, the "Services") are made 
            available to you subject to these Terms of Service (the "Terms"), which includes the Acceptable Use Policy, 
            and in accordance with Waka Running's Privacy Policy (the "Privacy Policy"). 
            BY ACCESSING, USING OR UPLOADING OR DOWNLOADING ANY INFORMATION OR MATERIALS TO OR FROM THE SERVICES, 
            OR BY INDICATING YOUR ASSENT TO THESE TERMS BY CREATING AN ACCOUNT, CLICKING "SIGN UP" OR ANY SIMILAR MECHANISM, 
            YOU ARE AGREEING TO THESE TERMS. IF YOU DO NOT AGREE TO THESE TERMS, DO NOT ACCESS OR USE THE SERVICES.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            Notice Regarding Dispute Resolution
          </Typography>
          <Typography variant="body2" paragraph>
            These Terms contain provisions that govern how disputes between you and Waka Running are resolved, including 
            an agreement to arbitrate, which will, unless you reside in the EU/EEA, with limited exception, require you to 
            submit claims you have against us to binding and final arbitration and limit you to claims against Waka Running 
            on an individual basis, unless you opt-out in accordance with the instructions below.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            Registration and Accounts
          </Typography>
          <Typography variant="body2" paragraph>
            The Services are intended solely for persons who are at least 13 years old or such higher age as required in your 
            country to use the Services. If you are under the legal age to form a legally binding contract in your jurisdiction, 
            you represent that your parent or legal guardian has reviewed and agreed to these Terms on your behalf.
          </Typography>
          <Typography variant="body2" paragraph>
            To use the Services, you must register. In doing so, you agree to: (a) provide true, accurate, current and complete 
            information about yourself ("User Data") and (b) maintain and promptly update the User Data. You agree that Running 
            Tracker may use your User Data to provide Services that you access or use and as otherwise set forth in these Terms 
            and our Privacy Policy. You also agree to create only one account for your own personal use and not share your account 
            with others.
          </Typography>
          <Typography variant="body2" paragraph>
            You are solely responsible for maintaining the confidentiality of the password associated with your account and for 
            restricting access to your password, your computer and mobile device while logged into the Services. You accept 
            responsibility for all activities that occur under your account or from your computer and mobile devices.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            Ownership of and Use of Content
          </Typography>
          <Typography variant="body2" paragraph>
            You own, or otherwise have the right to grant the rights and licenses set forth in these Terms, the information, data, 
            text, software, sound, photographs, graphics, video, messages, posts, tags, or other materials you make available in 
            connection with the Services ("Content"). You grant us a non-exclusive, transferable, sub-licensable, royalty-free, 
            worldwide license to use, reproduce, modify, adapt, publish, translate, create derivative works from, distribute, 
            publicly perform and display any Content and any name, username or likeness that you post on or in connection with 
            the Services in all media formats and channels now known or later developed without compensation to you. This license 
            ends when you delete your Content or your account.
          </Typography>
          <Typography variant="body2" paragraph>
            The Services are for your personal and noncommercial use. You may not modify, copy, distribute, transmit, display, 
            perform, reproduce, publish, license, create derivative works from, transfer or sell for any commercial purposes any 
            portion of the Services, use of the Services or access to Content.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            Content and Conduct Restrictions
          </Typography>
          <Typography variant="body2" paragraph>
            You understand that you, and not Waka Running, are entirely responsible for all Content that you upload, post, email, 
            transmit or otherwise make available via the Services. Waka Running may, in accordance with applicable law, screen, 
            monitor, hide, restrict, refuse or remove any Content that violates the Terms or is otherwise objectionable.
          </Typography>
          <Typography variant="body2" paragraph>
            You understand that by using the Services, you may be exposed to Content that is offensive, indecent, objectionable or 
            illegal. You agree to bear all risks associated with the use of any Content available in connection with the Services, 
            including any reliance on the accuracy, completeness, or usefulness of such Content.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            Disclaimer of Warranties and Liability
          </Typography>
          <Typography variant="body2" paragraph>
            THE INFORMATION, SOFTWARE, PRODUCTS, SERVICES AND CONTENT AVAILABLE THROUGH THE SERVICES ARE PROVIDED TO YOU "AS IS" 
            AND WITHOUT WARRANTY. RUNNING TRACKER AND ITS SUBSIDIARIES, AFFILIATES, OFFICERS, EMPLOYEES, AGENTS, PARTNERS AND 
            LICENSORS HEREBY DISCLAIM ALL WARRANTIES WITH REGARD TO SUCH INFORMATION, SOFTWARE, PRODUCTS, SERVICES AND CONTENT, 
            INCLUDING, WITHOUT LIMITATION, ALL IMPLIED WARRANTIES AND CONDITIONS OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, 
            TITLE AND NONINFRINGEMENT.
          </Typography>
          <Typography variant="body2" paragraph sx={{ fontWeight: 'bold' }}>
            YOU EXPRESSLY AGREE THAT YOUR ATHLETIC ACTIVITIES (INCLUDING, BUT NOT LIMITED TO, RUNNING, CYCLING, OR FOLLOWING A 
            TRAINING PLAN OFFERED ON THE SERVICES) CARRY CERTAIN INHERENT AND SIGNIFICANT RISKS OF PROPERTY DAMAGE, BODILY INJURY 
            OR DEATH AND THAT YOU VOLUNTARILY ASSUME ALL KNOWN AND UNKNOWN RISKS ASSOCIATED WITH THESE ACTIVITIES.
          </Typography>
          <Typography variant="body2" paragraph>
            YOU EXPRESSLY AGREE TO RELEASE RUNNING TRACKER, ITS SUBSIDIARIES, AFFILIATES, OFFICERS, AGENTS, REPRESENTATIVES, EMPLOYEES, 
            PARTNERS AND LICENSORS (THE "RELEASED PARTIES") FROM ANY AND ALL LIABILITY IN CONNECTION WITH YOUR ATHLETIC ACTIVITIES 
            AND/OR USE OF THE RUNNING TRACKER WEBSITES, MOBILE APPLICATIONS, CONTENT, SERVICES OR PRODUCTS.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            Indemnity
          </Typography>
          <Typography variant="body2" paragraph>
            You agree to indemnify and hold Waka Running and its subsidiaries, affiliates, directors, officers, agents, 
            representatives, employees, partners and licensors harmless from any claim or demand, including reasonable attorneys' fees, 
            made by any third party due to or arising out of Content you submit, post, transmit or otherwise seek to make available 
            through the Services, your use of the Services, your athletic activities which generate the Content you post or seek to 
            post on the Services, your connection to the Services, your violation of the Terms, your violation of any data protection 
            or privacy laws, or your violation of any rights of another person or entity.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            Dispute Resolution
          </Typography>
          <Typography variant="body2" paragraph>
            Except for disputes relating to Waka Running's intellectual property (such as trademarks, trade dress, domain names, 
            trade secrets, copyrights, and patents) or if you opt out of this agreement to arbitrate, all claims arising out of or 
            relating to this Agreement and your use of the Services shall be finally settled by binding arbitration administered by 
            JAMS in accordance with its provisions and procedures for consumer-related disputes, excluding any rules or procedures 
            governing or permitting class actions.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            Choice of Law and Forum
          </Typography>
          <Typography variant="body2" paragraph>
            Any action related to the Terms, Content, the Services, and your relationship with Waka Running shall be governed by, 
            and construed and interpreted in accordance with, the laws of the State of Indiana without regard to its conflict of laws 
            principles. Subject to the Section entitled "Dispute Resolution", the parties irrevocably consent to bring any action to 
            enforce this Agreement in the federal or state courts of Indiana and you consent to the exclusive jurisdiction of the 
            federal or state courts of Indiana.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            Termination
          </Typography>
          <Typography variant="body2" paragraph>
            You agree that Waka Running may, under certain circumstances and without prior notice, immediately suspend or terminate 
            your account and/or access to the Services. Cause for such suspension or termination shall include, but not be limited to, 
            (a) breaches or violations of the Terms or other incorporated agreements, policies or guidelines, (b) requests by law 
            enforcement or other government agencies, (c) a request by you (self-initiated account deletions), (d) discontinuance or 
            material modification to the Services (or any portion thereof), (e) unexpected technical or security issues or problems, 
            (f) extended periods of inactivity, and/or (g) nonpayment of any fees owed by you in connection with the Services.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            Modification of the Terms and Services
          </Typography>
          <Typography variant="body2" paragraph>
            Waka Running reserves the right to update the Terms at any time and for any reason in its sole discretion. Waka Running 
            Tracker will notify you in advance of any material changes to the Terms. By continuing to access or use the Services after 
            we have provided you with notice of a modification, you are agreeing to be bound by the modified Terms. If the modified 
            Terms are not acceptable to you, your only recourse is to cease using the Services and delete your account.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            Contact Information
          </Typography>
          <Typography variant="body2" paragraph>
            For questions regarding the Services and these Terms, please contact us at:
          </Typography>
          <Box sx={{ pl: 2, mb: 2 }}>
            <Typography variant="body2">
              Waka Running
              <br />
              3826 Dudley Dr.
              <br />
              Mishawaka, IN 46544
              <br />
              Phone: <Link href="tel:574-993-2331">574-993-2331</Link>
            </Typography>
          </Box>

          <Typography variant="body2" paragraph sx={{ mt: 3, fontStyle: 'italic' }}>
            By creating an account or using the Services, you acknowledge that you have read, understood, and agree to be bound by 
            these Terms of Service.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TermsOfServiceDialog;

